// Estado in-memory compartilhado dos seats da imobiliária (mock).
// Espelha o InMemoryAgencyInviteStore do agency-api (#162): convites de corretor
// CONSOMEM seats do plano contratado pela imobiliária (não geram créditos). Quando
// os seats se esgotam, o convite é bloqueado (NoSeatsAvailable). É a fonte única
// de verdade pros KPIs de licenças e pra lista de corretores convidados.
import type {
  AgencyBrokerListItem,
  BrokerLinkType,
} from "@/features/brokers/mocks/fixtures";

const TOTAL_SLOTS = 50;
const PLAN_NAME = "Agency Pro";
const EXPIRES_AT = new Date(2027, 4, 27).toISOString();

let usedSlots = 32;
let inviteSeq = 0;

// Seed: um convite pendente já existente (mesmo corretor que antes vinha fixo na
// lista de brokers). Fica no store pra que reenviar/revogar funcionem de verdade.
const invitedBrokers: AgencyBrokerListItem[] = [
  {
    id: "invite-seed-1",
    name: "Aguardando Cadastro",
    email: "juliana@exemplo.com",
    hasActiveLicense: false,
    status: "invited",
    joinedAt: new Date(2026, 4, 25, 11, 0, 0).toISOString(),
    productionVgv: 0,
  },
];

export class NoSeatsAvailableError extends Error {
  constructor() {
    super("Não há seats disponíveis no plano da imobiliária.");
    this.name = "NoSeatsAvailableError";
  }
}

export interface AgencySeatSummary {
  totalSlots: number;
  usedSlots: number;
  availableSlots: number;
  planName: string;
  expiresAt: string;
}

export function getSeatSummary(): AgencySeatSummary {
  return {
    totalSlots: TOTAL_SLOTS,
    usedSlots,
    availableSlots: TOTAL_SLOTS - usedSlots,
    planName: PLAN_NAME,
    expiresAt: EXPIRES_AT,
  };
}

// Consome um seat e registra o convite. Lança NoSeatsAvailableError quando o
// plano já está no limite — mesma regra do agency-api.
export function consumeSeatForInvite(
  email: string,
  linkType: BrokerLinkType,
): AgencyBrokerListItem {
  if (TOTAL_SLOTS - usedSlots <= 0) throw new NoSeatsAvailableError();

  usedSlots += 1;
  inviteSeq += 1;

  const broker: AgencyBrokerListItem = {
    id: `invite-${inviteSeq}`,
    name: "Aguardando Cadastro",
    email,
    hasActiveLicense: false,
    status: "invited",
    linkType,
    joinedAt: new Date().toISOString(),
    productionVgv: 0,
  };
  invitedBrokers.push(broker);
  return broker;
}

export function listInvitedBrokers(): AgencyBrokerListItem[] {
  return invitedBrokers;
}

export class InviteNotFoundError extends Error {
  constructor() {
    super("Convite não encontrado.");
    this.name = "InviteNotFoundError";
  }
}

// Reenvia o convite (apenas "toca" a data — mock). Não consome novo seat.
export function resendInvite(inviteId: string): AgencyBrokerListItem {
  const invite = invitedBrokers.find((b) => b.id === inviteId);
  if (!invite) throw new InviteNotFoundError();
  invite.joinedAt = new Date().toISOString();
  return invite;
}

// Revoga o convite e DEVOLVE o seat ao plano (usedSlots decrementa).
export function revokeInvite(inviteId: string): void {
  const idx = invitedBrokers.findIndex((b) => b.id === inviteId);
  if (idx === -1) throw new InviteNotFoundError();
  invitedBrokers.splice(idx, 1);
  usedSlots = Math.max(0, usedSlots - 1);
}
